import { Model, Factory, belongsTo, hasMany, trait } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randPastDate, randRecentDate } from '@ngneat/falso'
import type { MockServer, MirageServer } from './../server.js'
import type { SchemaAttributes } from '../../types.js'
import { createHtmlParagraph, createErrorResponse } from './../utils.js'

export type ConsultationDischargeInstructionData =
  SchemaAttributes<'ConsultationDischargeInstruction'>

export const model = (
  Model as ModelDefinition<ConsultationDischargeInstructionData>
).extend({
  consultation: belongsTo('consultation'),
  patient: belongsTo('patient'),
  createdUser: belongsTo('user'),
  modifiedUser: belongsTo('user'),
  attachments: hasMany('upload'),
})

export const factory = Factory.extend<ConsultationDischargeInstructionData>({
  text() {
    return createHtmlParagraph()
  },
  created() {
    return randPastDate().toISOString()
  },
  modified() {
    return randRecentDate().toISOString()
  },
  withCreatedUser: trait({
    afterCreate(instruction, server) {
      const createdUser = server.create('user')
      instruction.update({ createdUser })
    },
  }),
  withModifiedUser: trait({
    afterCreate(instruction, server) {
      const modifiedUser = server.create('user')
      instruction.update({ modifiedUser })
    },
  }),
  withPatient: trait({
    afterCreate(instruction, server) {
      const patient = server.create('patient')
      instruction.update({ patient })
    },
  }),
})

export function routes(server: MockServer) {
  server.get(
    '/consultations/:parent_lookup_consultation/discharge_instructions',
    function (this: MirageServer, schema, request) {
      const consultationId = request.params.parent_lookup_consultation
      const instructions = schema.where('consultationDischargeinstruction', {
        consultationId,
      })
      return this.serialize(instructions)
    },
  )

  server.post(
    '/consultations/:parent_lookup_consultation/discharge_instructions',
    function (schema, request) {
      const attrs = JSON.parse(request.requestBody)
      if (!attrs?.data?.attributes) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Missing "attributes" in request body' }],
        })
      }
      const relationships = attrs.data.relationships
      const consultationId = request.params.parent_lookup_consultation

      const instruction = schema.create(
        'consultationDischargeinstruction',
        attrs.data.attributes,
      )

      if (consultationId) {
        const consultation = schema.find('consultation', consultationId)
        instruction.update({ consultation })
      }

      if (relationships.patient.data) {
        const patient = schema.find('patient', relationships.patient.data.id)
        instruction.update({ patient })
      }

      return instruction
    },
  )

  server.patch('/discharge_instructions/:id', function (schema, request) {
    const attrs = JSON.parse(request.requestBody)
    if (!attrs?.data?.attributes) {
      return createErrorResponse({
        status: 400,
        errors: [{ detail: 'Missing "attributes" in request body' }],
      })
    }
    const id = request.params.id
    const instruction = schema.find('consultationDischargeinstruction', id)
    if (!instruction) {
      return createErrorResponse({
        status: 404,
        errors: [
          {
            detail: `Consultation discharge instruction with id ${id} not found`,
          },
        ],
      })
    }

    const { upload_group_id, ...restAttributes } = attrs.data.attributes
    instruction.update(restAttributes)

    // Associate uploads from the group with this instruction
    if (upload_group_id) {
      const uploadGroup = schema.find('uploadGroup', upload_group_id)
      if (uploadGroup) {
        const uploads = schema.where('upload', {
          uploadGroupId: upload_group_id,
        })
        const existingAttachments = instruction.attachments?.models ?? []
        const allAttachments = [...existingAttachments, ...uploads.models]
        instruction.update({
          attachmentIds: allAttachments.map((a: { id: string }) => a.id),
        })
      }
    }

    return instruction
  })
}

export function seeds(server: MockServer) {
  server.schema.all('consultation').models.forEach((consultation) => {
    const instructions = server.createList(
      'consultationDischargeinstruction',
      1,
      'withPatient',
      'withCreatedUser',
      'withModifiedUser',
      {
        consultation,
      },
    )

    // Seed each instruction with mock attachments
    for (const instruction of instructions) {
      const uploadGroup = server.create('uploadGroup')
      const uploads = server.createList('upload', 2, { uploadGroup })
      instruction.update({ attachmentIds: uploads.map((u) => u.id) })
    }
  })
}
